import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Link as LinkIcon, Mail, Shield, Trash2, Copy, CheckCircle2, Users, Heart } from 'lucide-react';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { ArchiveContextNav } from '../components/archive/ArchiveContextNav';
import { treeHref } from '../utils/prettyArchiveLinks';
import { PERMISSION_LEVELS, ROLE_TO_PERMISSION } from '../utils/archivePrivacy';
import { ArchiveActivityLog } from '../components/archive/ArchiveActivityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TreeMembers = () => {
  const { treeId: routeTreeKey, familySlug } = useParams();
  const routeTreeParam = routeTreeKey || familySlug;
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState('viewer');
  const [invitePermission, setInvitePermission] = useState('view');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copiedToken, setCopiedToken] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  
  // Email invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [currentInvitationId, setCurrentInvitationId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [treeRes, membersRes, invitationsRes] = await Promise.all([
        axios.get(`${API}/trees/${routeTreeParam}`),
        axios.get(`${API}/trees/${routeTreeParam}/members`),
        axios.get(`${API}/trees/${routeTreeParam}/invitations`)
      ]);
      setTree(treeRes.data);
      setMembers(membersRes.data);
      setInvitations(invitationsRes.data);
    } catch (error) {
      toast.error('Failed to load members');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [routeTreeParam, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateLink = async () => {
    if (!tree?.id) return;
    setGeneratingLink(true);
    try {
      const response = await axios.post(`${API}/trees/${tree.id}/invite`, {
        email: null,
        role: invitePermission === 'contribute' ? 'editor' : 'viewer',
        permission_level: invitePermission,
        generate_link: true
      });
      
      const inviteUrl = `${window.location.origin}/accept-invite/${response.data.token}`;
      setGeneratedLink(inviteUrl);
      setCurrentInvitationId(response.data.id);
      setInvitations([...invitations, response.data]);
      toast.success('Invitation link created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleSendEmail = async () => {
    if (!inviteEmail || !currentInvitationId) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/invitations/${currentInvitationId}/send-email`, {
        recipient_email: inviteEmail
      });
      setEmailSent(true);
      const msg =
        res.data?.message ||
        (res.status === 202 ? `Invitation queued for ${inviteEmail} — check your inbox shortly.` : `Invitation sent to ${inviteEmail}`);
      toast.success(msg);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unable to send email';
      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleUpdateRole = async (userId, newPermission) => {
    try {
      const role = newPermission === 'contribute' ? 'editor' : 'viewer';
      await axios.put(`${API}/trees/${tree.id}/members/${userId}/role`, {
        role,
        permission_level: newPermission,
      });
      toast.success('Access updated');
      setMembers(members.map(m => m.user_id === userId ? { ...m, role, permission_level: newPermission } : m));
    } catch (error) {
      toast.error('Failed to update access');
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from this tree?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/trees/${tree.id}/members/${userId}`);
      toast.success('Member removed');
      setMembers(members.filter(m => m.user_id !== userId));
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const copyToClipboard = (text, token) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(token);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedToken(''), 2000);
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner: 'bg-primary/10 text-primary border-primary/20',
      editor: 'bg-green-500/10 text-green-700 border-green-500/20 dark:text-green-400',
      viewer: 'bg-muted text-muted-foreground border-border'
    };
    return colors[role] || colors.viewer;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl font-serif">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <ArchiveContextNav
        treeId={tree?.id || routeTreeParam}
        treeLinkBase={tree ? treeHref(tree) : undefined}
        treeName={tree?.name}
        variant="tree"
        canExport={false}
      />
      <div className="flex min-w-0 flex-1 flex-col pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] lg:pb-0">
      <nav className="border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="hidden text-lg transition-colors hover:opacity-80 sm:block"
                data-testid="home-link"
              >
                <BrandWordmark variant="treeNav" className="text-lg" />
              </button>
              <span className="text-muted-foreground/40 hidden sm:block">/</span>
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="sm:hidden" data-testid="back-btn-mobile">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-serif font-semibold" data-testid="tree-name">{tree?.name}</h1>
                <p className="text-sm text-muted-foreground">{members.length} {members.length === 1 ? 'member' : 'members'} collaborating</p>
              </div>
            </div>
            <Dialog open={isInviteDialogOpen} onOpenChange={(open) => {
              setIsInviteDialogOpen(open);
              if (!open) {
                setGeneratedLink('');
                setInviteRole('viewer');
                setInviteEmail('');
                setEmailSent(false);
                setCurrentInvitationId(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="invite-btn">
                  <Heart className="mr-2 h-5 w-5" /> Invite Family
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md" data-testid="invite-dialog">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">Invite Family Members</DialogTitle>
                  <DialogDescription className="text-base">
                    Invite loved ones to help preserve these stories together.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Role Selection */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">What can they do?</Label>
                    <p className="text-xs text-muted-foreground">
                      Collaborators see only this tree. They cannot access your other trees or account settings.
                    </p>
                    <Select
                      value={invitePermission}
                      onValueChange={(v) => {
                        setInvitePermission(v);
                        setInviteRole(v === 'contribute' ? 'editor' : 'viewer');
                      }}
                    >
                      <SelectTrigger data-testid="invite-role-select" className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERMISSION_LEVELS.filter((p) => p.id !== 'manage').map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            <div className="flex flex-col items-start py-1">
                              <span className="font-medium">{p.label}</span>
                              <span className="text-xs text-muted-foreground">{p.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Link Generation */}
                  {!generatedLink ? (
                    <div className="space-y-3">
                      <Button 
                        onClick={handleGenerateLink} 
                        className="w-full h-12 rounded-full" 
                        disabled={generatingLink}
                        data-testid="generate-link-btn"
                      >
                        {generatingLink ? (
                          <>Creating link...</>
                        ) : (
                          <>
                            <LinkIcon className="mr-2 h-5 w-5" />
                            Create Invitation Link
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-center text-muted-foreground">
                        You'll get a link to share via text, email, or any way you prefer.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3">
                        <div className="flex items-center gap-2 text-primary">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-medium">Link ready to share!</span>
                        </div>
                        <div className="flex gap-2">
                          <Input 
                            value={generatedLink} 
                            readOnly 
                            className="text-sm bg-background"
                            data-testid="generated-link" 
                          />
                          <Button 
                            type="button" 
                            onClick={() => copyToClipboard(generatedLink, 'link')}
                            className="shrink-0"
                            data-testid="copy-link-btn"
                          >
                            {copiedToken === 'link' ? (
                              <>
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Anyone with this link can join as <span className="font-medium">{inviteRole}</span>. 
                          Send it via text message, email, or however you like.
                        </p>
                      </div>
                      
                      {/* Email Send Option */}
                      {!emailSent ? (
                        <div className="border border-border/50 rounded-xl p-4 space-y-3">
                          <div className="flex items-center gap-2 text-foreground">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Or send via email</span>
                          </div>
                          <div className="flex gap-2">
                            <Input 
                              type="email"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              placeholder="family@example.com"
                              className="text-sm"
                              data-testid="invite-email-input"
                            />
                            <Button 
                              type="button" 
                              variant="outline"
                              onClick={handleSendEmail}
                              disabled={!inviteEmail || sendingEmail}
                              className="shrink-0"
                              data-testid="send-email-btn"
                            >
                              {sendingEmail ? (
                                <>Sending...</>
                              ) : (
                                <>
                                  <Mail className="mr-2 h-4 w-4" />
                                  Send
                                </>
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Email delivery will activate once domain verification is complete.
                          </p>
                        </div>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="font-medium">Invitation sent to {inviteEmail}</span>
                          </div>
                          <p className="text-xs text-green-600 mt-1">
                            They'll receive a simple email with a link to join.
                          </p>
                        </div>
                      )}
                      
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setGeneratedLink('');
                          setInviteRole('viewer');
                          setInviteEmail('');
                          setEmailSent(false);
                          setCurrentInvitationId(null);
                        }}
                        className="w-full"
                      >
                        Create Another Invitation
                      </Button>
                    </div>
                  )}

                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Collaboration Banner */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-serif font-semibold mb-1">Build Your Family Archive Together</h2>
              <p className="text-sm text-muted-foreground">
                Invite family members to help add people, record stories, and grow this archive together. 
                Collaborators see only this tree—not your other trees or account. You control who has access.
              </p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="members" className="space-y-6">
          <TabsList>
            <TabsTrigger value="members" data-testid="members-tab">
              <Users className="h-4 w-4 mr-2" />
              Members ({members.length})
            </TabsTrigger>
            <TabsTrigger value="invitations" data-testid="invitations-tab">
              <LinkIcon className="h-4 w-4 mr-2" />
              Pending Invites ({invitations.length})
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="activity-tab">
              Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members" data-testid="members-list">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif">Family Collaborators</CardTitle>
                <CardDescription>People who have access to this family tree</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 border border-border rounded-lg" data-testid={`member-${member.user_id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {member.user_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold" data-testid={`member-name-${member.user_id}`}>{member.user_name}</p>
                          <p className="text-sm text-muted-foreground" data-testid={`member-email-${member.user_id}`}>{member.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.role === 'owner' ? (
                          <Badge className={getRoleBadge(member.role)} data-testid={`member-role-${member.user_id}`}>
                            <Shield className="h-3 w-3 mr-1" />
                            Tree creator
                          </Badge>
                        ) : (
                          <>
                            <Select
                              value={member.permission_level || ROLE_TO_PERMISSION[member.role] || 'view'}
                              onValueChange={(value) => handleUpdateRole(member.user_id, value)}
                            >
                              <SelectTrigger className="w-36" data-testid={`role-select-${member.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PERMISSION_LEVELS.filter((p) => p.id !== 'manage').map((p) => (
                                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveMember(member.user_id, member.user_name)}
                              data-testid={`remove-member-${member.user_id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invitations" data-testid="invitations-list">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif">Pending Invitations</CardTitle>
                <CardDescription>Links you've shared that haven't been used yet</CardDescription>
              </CardHeader>
              <CardContent>
                {invitations.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground" data-testid="no-invitations">
                    <LinkIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p className="font-medium mb-1">No pending invitations</p>
                    <p className="text-sm">Create an invitation link to share with family members.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invitations.map((inv) => {
                      const inviteUrl = `${window.location.origin}/accept-invite/${inv.token}`;
                      return (
                        <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-border rounded-xl gap-4" data-testid={`invitation-${inv.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-muted rounded-lg">
                              <LinkIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">Shareable Link</p>
                              <p className="text-sm text-muted-foreground">Anyone with this link can join as {inv.role}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleBadge(inv.role)} data-testid={`invitation-role-${inv.id}`}>
                              {inv.role === 'editor' ? 'Can Edit' : 'Can View'}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(inviteUrl, inv.token)}
                              className="rounded-full"
                              data-testid={`copy-invitation-${inv.id}`}
                            >
                              {copiedToken === inv.token ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy Link
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" data-testid="activity-list">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif">Collaboration activity</CardTitle>
                <CardDescription>Who changed what and when — invites, privacy, exports, and overlap reviews.</CardDescription>
              </CardHeader>
              <CardContent>
                <ArchiveActivityLog treeId={tree?.id} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      </div>
    </div>
  );
};

export default TreeMembers;